/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "../../components";
import { useAlert } from "../../store/useAlert";
import { useAuth } from "../../store/useAuth";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";

interface ReviewAccount {
  _id: string;
  name: string;
  email: string | null;
  phone_number: string | null;
  user_role: number | null;
  createdAt: string | null;
  balance: number;
  drops: number;
  lastDropAt: string | null;
  emailVerified: boolean;
  phoneVerified: boolean;
  // Google or Apple sign-ins linked to the account ("google.com", "apple.com").
  providers: string[];
  // An old Google or Apple link that is made on the account's next sign-in.
  oldSocialLink: boolean;
  pendingDelete: boolean;
  blocked: boolean;
}

interface DuplicateGroup {
  matchedOn: "email" | "phone";
  value: string;
  since: string | null;
  accounts: ReviewAccount[];
}

interface Mismatch {
  account: ReviewAccount;
  provider: string | null;
  reason: string | null;
  count: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

// A phone number (proved with a code) or a Google/Apple sign-in a person added
// to their account while another account held it.
interface Claim {
  kind: "phone_claim" | "sign_in_claim";
  method: string | null;
  value: string | null;
  outcome: "review" | "moved" | "merged" | "dismissed" | null;
  reason: string | null;
  // Customer service rejected it (this one, or an earlier claim of the account).
  dismissedAt: string | null;
  // A Google/Apple claim: that account's email, to compare with both accounts.
  email?: string | null;
  emailVerified?: boolean | null;
  claimant: ReviewAccount | null;
  other: ReviewAccount | null;
  count: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

interface AccountReview {
  generatedAt: string;
  duplicates: DuplicateGroup[];
  mismatches: Mismatch[];
  claims?: Claim[];
  settled?: Claim[];
}

type Tab = "duplicates" | "mismatches" | "claims";

const ROLE_NAMES: Record<number, string> = {
  1: "Admin",
  2: "Employee",
  3: "Customer",
  4: "External viewer",
  5: "Store admin",
  6: "Plastic viewer",
  7: "Super admin",
};

const PROVIDER_NAMES: Record<string, string> = {
  "google.com": "Google",
  "apple.com": "Apple",
};

const REASONS: Record<string, string> = {
  "email-differs": "their Google/Apple email is not the account's email",
  "email-unverified": "their Google/Apple email is not verified",
};

const METHOD_NAMES: Record<string, string> = {
  phone: "Phone number",
  ...PROVIDER_NAMES,
};

const OUTCOMES: Record<string, string> = {
  moved: "Number moved here from an account that signs in with Google/Apple",
  merged: "Empty account with no other way in merged into this one",
  dismissed: "Rejected by customer service; nothing moved",
};

const formatEuro = (cents?: number) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(
    Number(cents ?? 0) / 100
  );

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("DD/MM/YYYY") : "-";

const signInNames = (account: ReviewAccount) => {
  const names = account.providers.map((p) => PROVIDER_NAMES[p] ?? p);
  if (account.oldSocialLink) names.push("Google or Apple (old link)");
  if (account.phone_number) names.push("Phone");
  return names.length ? names.join(", ") : "-";
};

/**
 * The account a merge should keep: the one in use. Most drops, then the higher
 * balance, then the older account. Only a suggestion; the merge page can swap.
 */
const suggestedKeep = (accounts: ReviewAccount[]) =>
  [...accounts].sort(
    (a, b) =>
      b.drops - a.drops ||
      b.balance - a.balance ||
      +new Date(a.createdAt ?? 0) - +new Date(b.createdAt ?? 0)
  )[0];

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wide text-gray-text">{label}</dt>
    <dd className="text-sm text-dark break-words">{children}</dd>
  </div>
);

const WithMark = ({ value, verified }: { value: string | null; verified: boolean }) => (
  <>
    <span className="break-all">{value ?? "-"}</span>
    {value && verified ? <span className="text-xs text-gray-text"> (verified)</span> : null}
  </>
);

const AccountName = ({ account }: { account: ReviewAccount }) => (
  <div className="min-w-0">
    <Link
      to={`/edit-user/${account._id}`}
      className="font-satoshi font-bold text-base text-dark underline underline-offset-2 break-words"
    >
      {account.name || "Unnamed user"}
    </Link>
    <p className="text-xs text-gray-text">
      {ROLE_NAMES[account.user_role ?? -1] ?? "-"} · joined {formatDate(account.createdAt)}
    </p>
  </div>
);

function AccountTile({
  account,
  keep,
  onMerge,
}: {
  account: ReviewAccount;
  keep: boolean;
  onMerge?: () => void;
}) {
  const status = [account.pendingDelete && "Pending delete", account.blocked && "Blocked"]
    .filter(Boolean)
    .join(", ");
  return (
    <div
      className={`rounded-lg border p-4 flex flex-col gap-4 ${
        keep ? "border-green-500 bg-green-50/40" : "border-gray-border bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <AccountName account={account} />
        {keep ? (
          <span className="shrink-0 rounded-full bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1">
            Suggested to keep
          </span>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Fact label="Drops">{account.drops}</Fact>
        <Fact label="Balance">{formatEuro(account.balance)}</Fact>
        <Fact label="Last drop">{formatDate(account.lastDropAt)}</Fact>
        <Fact label="Signs in with">{signInNames(account)}</Fact>
        <Fact label="Email">
          <WithMark value={account.email} verified={account.emailVerified} />
        </Fact>
        <Fact label="Phone">
          <WithMark value={account.phone_number} verified={account.phoneVerified} />
        </Fact>
        {status ? <Fact label="Status">{status}</Fact> : null}
      </dl>
      {onMerge ? (
        <div className="mt-auto pt-1">
          <PrimaryButton label="Merge into the suggested account" size="sm" onClick={onMerge} />
        </div>
      ) : null}
    </div>
  );
}

function AccountReviewScreen() {
  const { user: currentUser } = useAuth();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const isSuperAdmin = Number(currentUser?.user_role) === 7;

  const [review, setReview] = useState<AccountReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<Tab>("duplicates");

  const load = async () => {
    setLoading(true);
    try {
      const response = await axios.get("users/account-review");
      // axiosConfig answers a copy of a request that is still running with
      // undefined; the running one fills the page.
      if (response) setReview(response.data);
    } catch (error: any) {
      // axiosConfig rejects with the response itself.
      setAlert({
        isOpen: true,
        message:
          error?.data?.message ??
          error?.response?.data?.message ??
          "Could not load the account review.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) load();
  }, [isSuperAdmin]);

  // Super admins only, like the endpoint behind it.
  if (!isSuperAdmin) {
    return <Navigate to="/users" replace />;
  }

  // Two different people (for example a shared phone): nothing moves, the claim
  // leaves the list and the person's app stops showing it as being added.
  const rejectClaim = async (c: Claim) => {
    if (!c.claimant) return;
    const what = c.kind === "phone_claim" ? "number" : "sign-in";
    const ok = window.confirm(
      `Reject this claim? Nothing moves: the ${what} stays where it is, and the person's app ` +
        "stops showing it as being added. Let them know yourself if needed."
    );
    if (!ok) return;
    try {
      // lastSeenAt: the claim as shown here; one made again since is not rejected unseen.
      await axios.post("users/account-review/claims/dismiss", {
        userId: c.claimant._id,
        kind: c.kind,
        lastSeenAt: c.lastSeenAt,
      });
      setAlert({ isOpen: true, message: "The claim was rejected.", severity: "success" });
      load();
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error?.data?.message ?? error?.response?.data?.message ?? "Could not reject the claim.",
        severity: "error",
      });
    }
  };

  const duplicates = review?.duplicates ?? [];
  const mismatches = review?.mismatches ?? [];
  const claims = review?.claims ?? [];
  const settled = review?.settled ?? [];
  const byEmail = duplicates.filter((g) => g.matchedOn === "email").length;
  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "duplicates", label: "Duplicate accounts", count: duplicates.length },
    { key: "mismatches", label: "Sign-in email differs", count: mismatches.length },
    { key: "claims", label: "Claimed sign-ins", count: claims.length },
  ];

  return (
    <div>
      <div className="flex w-full flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6">
        <div>
          <h1 className="font-satoshi text-2xl font-bold text-dark">Account review</h1>
          <p className="text-sm text-gray-text mt-2 max-w-3xl">
            Accounts that may belong to the same person, Google or Apple sign-ins
            that found an account with a different email, and sign-ins people added
            while another account held them. Nothing here is blocked:
            these accounts keep working. Customer service settles each case by hand,
            usually with a merge.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {review ? (
            <span className="text-xs text-gray-text">
              Updated {dayjs(review.generatedAt).format("DD/MM/YYYY HH:mm")}
            </span>
          ) : null}
          <SecondaryButton label={loading ? "Loading..." : "Refresh"} onClick={load} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-full border px-4 py-2 text-sm font-satoshi font-medium ${
              tab === t.key
                ? "bg-dark text-white border-dark"
                : "bg-white text-dark border-gray-border hover:bg-gray-50"
            }`}
          >
            {t.label} <span className={tab === t.key ? "text-white/80" : "text-gray-text"}>{t.count}</span>
          </button>
        ))}
      </div>

      {!review ? (
        <p className="text-sm text-gray-text">Loading...</p>
      ) : tab === "duplicates" ? (
        <div className="space-y-5">
          <p className="text-sm text-gray-text">
            {duplicates.length} groups: {byEmail} share an email (in another letter case),{" "}
            {duplicates.length - byEmail} share a phone number. The account with the most
            drops is suggested to keep; the merge page lets you swap before anything changes.
          </p>
          {!duplicates.length ? (
            <div className="bg-white rounded-xl border border-gray-border px-6 py-8 text-sm text-gray-text">
              No duplicate accounts.
            </div>
          ) : (
            duplicates.map((group) => {
              const keep = suggestedKeep(group.accounts);
              return (
                <section
                  key={`${group.matchedOn}:${group.value}`}
                  className="bg-white rounded-xl border border-gray-border px-5 py-5 sm:px-6"
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
                    <span className="rounded-full bg-droppie-lichtblauw text-dark text-xs font-medium px-2.5 py-1">
                      {group.matchedOn === "email" ? "Same email" : "Same phone number"}
                    </span>
                    <span className="font-satoshi font-medium text-dark break-all">{group.value}</span>
                    <span className="text-xs text-gray-text">since {formatDate(group.since)}</span>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {group.accounts.map((account) => (
                      <AccountTile
                        key={account._id}
                        account={account}
                        keep={account._id === keep._id}
                        onMerge={
                          account._id === keep._id
                            ? undefined
                            : () => navigate(`/merge-accounts?keep=${keep._id}&merge=${account._id}`)
                        }
                      />
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </div>
      ) : tab === "claims" ? (
        <div className="space-y-5">
          <p className="text-sm text-gray-text max-w-3xl">
            A person proved a phone number (with a code) or a Google/Apple sign-in to add
            it to their account, while another account holds it. Nothing was moved; the
            person was told it will be added within a few working days. Usually the other
            account is their own older one: merge them. If they are two different people
            (for example a shared phone), reject the claim. A case leaves this list once the
            person's account holds it, it is rejected, or either account is gone.
          </p>
          {!claims.length ? (
            <div className="bg-white rounded-xl border border-gray-border px-6 py-8 text-sm text-gray-text">
              No claims waiting.
            </div>
          ) : (
            claims.map((c) => {
              const pair = [c.claimant, c.other].filter(Boolean) as ReviewAccount[];
              const keep = suggestedKeep(pair);
              return (
                <section
                  key={`${c.kind}:${c.claimant?._id}`}
                  className="bg-white rounded-xl border border-gray-border px-5 py-5 sm:px-6"
                >
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                    <span className="rounded-full bg-droppie-lichtblauw text-dark text-xs font-medium px-2.5 py-1">
                      {METHOD_NAMES[c.method ?? ""] ?? c.method ?? "-"}
                    </span>
                    {c.value ? (
                      <span className="font-satoshi font-medium text-dark break-all">{c.value}</span>
                    ) : null}
                    <span className="text-xs text-gray-text">
                      proved on {formatDate(c.lastSeenAt)}
                      {c.count > 1 ? ` (${c.count} times)` : ""}
                      {c.dismissedAt ? `, rejected before on ${formatDate(c.dismissedAt)}` : ""}
                    </span>
                  </div>
                  {c.kind === "sign_in_claim" && c.email ? (
                    <p className="text-sm text-dark mb-1">
                      Signed in as <span className="break-all">{c.email}</span>
                      {c.emailVerified ? <span className="text-xs text-gray-text"> (verified)</span> : null}
                    </p>
                  ) : null}
                  {c.reason ? (
                    <p className="text-xs text-gray-text mb-4">Not settled on its own: {c.reason}.</p>
                  ) : null}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {pair.map((account) => (
                      <div key={account._id} className="flex flex-col gap-2">
                        <span className="text-xs font-medium text-gray-text">
                          {account._id === c.claimant?._id ? "Proved it and wants it added" : "Holds it now"}
                        </span>
                        <AccountTile
                          account={account}
                          keep={account._id === keep._id}
                          onMerge={
                            account._id === keep._id
                              ? undefined
                              : () => navigate(`/merge-accounts?keep=${keep._id}&merge=${account._id}`)
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex justify-end">
                    <SecondaryButton label="Reject this claim" onClick={() => rejectClaim(c)} />
                  </div>
                </section>
              );
            })
          )}
          <h2 className="font-satoshi text-lg font-bold text-dark pt-4">Settled (last 30 days)</h2>
          {!settled.length ? (
            <div className="bg-white rounded-xl border border-gray-border px-6 py-6 text-sm text-gray-text">
              Nothing settled yet.
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-border divide-y divide-gray-border">
              {settled.map((c) => (
                <div
                  key={`${c.kind}:${c.claimant?._id ?? c.lastSeenAt}`}
                  className="px-5 py-3 sm:px-6 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm"
                >
                  <span className="text-xs text-gray-text w-24 shrink-0">
                    {formatDate(c.outcome === "dismissed" ? c.dismissedAt : c.lastSeenAt)}
                  </span>
                  <span className="w-32 shrink-0">{METHOD_NAMES[c.method ?? ""] ?? c.method ?? "-"}</span>
                  <span className="flex-1 text-dark">{OUTCOMES[c.outcome ?? ""] ?? c.outcome}</span>
                  <span className="min-w-0 break-words">
                    {c.claimant ? (
                      <Link to={`/edit-user/${c.claimant._id}`} className="underline underline-offset-2">
                        {c.claimant.name || "Unnamed user"}
                      </Link>
                    ) : (
                      "account gone"
                    )}
                    {c.other ? (
                      <span className="text-gray-text">
                        {" "}from{" "}
                        <Link to={`/edit-user/${c.other._id}`} className="underline underline-offset-2">
                          {c.other.name || "Unnamed user"}
                        </Link>
                      </span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-text max-w-3xl">
            A Google or Apple sign-in found the account through its old link, but the
            account's email is different. The person was let in. A case leaves this
            list once the sign-in is linked to the account, for example after the email
            is corrected and they sign in again.
          </p>
          {!mismatches.length ? (
            <div className="bg-white rounded-xl border border-gray-border px-6 py-8 text-sm text-gray-text">
              No sign-in mismatches.
            </div>
          ) : (
            mismatches.map((m) => (
              <section
                key={m.account._id}
                className="bg-white rounded-xl border border-gray-border px-5 py-5 sm:px-6 flex flex-col gap-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <AccountName account={m.account} />
                  <span className="text-sm text-dark">
                    Signed in with {PROVIDER_NAMES[m.provider ?? ""] ?? m.provider ?? "-"}:{" "}
                    {REASONS[m.reason ?? ""] ?? m.reason ?? "-"}
                  </span>
                </div>
                <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-3">
                  <Fact label="Email">
                    <WithMark value={m.account.email} verified={m.account.emailVerified} />
                  </Fact>
                  <Fact label="Phone">
                    <WithMark value={m.account.phone_number} verified={m.account.phoneVerified} />
                  </Fact>
                  <Fact label="Times">{m.count}</Fact>
                  <Fact label="First / last seen">
                    {formatDate(m.firstSeenAt)} / {formatDate(m.lastSeenAt)}
                  </Fact>
                  <Fact label="Drops">{m.account.drops}</Fact>
                  <Fact label="Balance">{formatEuro(m.account.balance)}</Fact>
                </dl>
              </section>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default withProtectedRoute(AccountReviewScreen);
